import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE from '../config';

const ITEM_LABELS = {
  computer: 'PC / Компютър',
  monitor1: 'Monitor 1',
  monitor2: 'Monitor 2',
  token: 'MFA Token / OneSpan',
  other: 'Other / Друго',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB') + ' ' +
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function SignReturn() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | signed | already | expired | error
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [typedName, setTypedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/sign/return/${token}`)
      .then(r => r.json())
      .then(res => {
        if (res.success) { setData(res.data); setStatus('ready'); }
        else if (res.message?.includes('already')) setStatus('already');
        else if (res.message?.includes('expired')) setStatus('expired');
        else { setErrorMsg(res.message || 'Invalid link.'); setStatus('error'); }
      })
      .catch(() => { setErrorMsg('Could not connect to server.'); setStatus('error'); });
  }, [token]);

  const handleConfirm = async () => {
    setNameError('');
    if (!typedName.trim()) { setNameError('Please type your full name to confirm.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/sign/return/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typedName: typedName.trim() })
      });
      const result = await res.json();
      if (res.ok) { setStatus('signed'); }
      else { setNameError(result.message || 'Confirmation failed.'); }
    } catch {
      setNameError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') return (
    <div style={styles.page}>
      <div style={styles.card}><p style={{ textAlign: 'center', color: '#555' }}>Loading…</p></div>
    </div>
  );

  if (status === 'already') return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ ...styles.header, background: 'linear-gradient(135deg,#065f46,#047857)' }}>
          <h2 style={styles.headerTitle}>✅ Already Signed / Вече подписан</h2>
        </div>
        <div style={styles.body}>
          <p>This return document has already been signed.</p>
          <p>Този документ вече е подписан.</p>
        </div>
      </div>
    </div>
  );

  if (status === 'expired') return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ ...styles.header, background: 'linear-gradient(135deg,#92400e,#b45309)' }}>
          <h2 style={styles.headerTitle}>⏰ Link Expired / Препратката е изтекла</h2>
        </div>
        <div style={styles.body}>
          <p>This signature link has expired. Please contact your IT administrator.</p>
          <p>Тази препратка е изтекла. Свържете се с вашия IT администратор.</p>
        </div>
      </div>
    </div>
  );

  if (status === 'error') return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ ...styles.header, background: 'linear-gradient(135deg,#7f1d1d,#991b1b)' }}>
          <h2 style={styles.headerTitle}>⚠ Invalid Link</h2>
        </div>
        <div style={styles.body}><p>{errorMsg}</p></div>
      </div>
    </div>
  );

  if (status === 'signed') return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ ...styles.header, background: 'linear-gradient(135deg,#065f46,#047857)' }}>
          <h2 style={styles.headerTitle}>✅ Return Confirmed / Връщането е потвърдено</h2>
        </div>
        <div style={styles.body}>
          <p>Thank you, <strong>{data?.employeeName}</strong>! Your equipment return has been officially confirmed.</p>
          <p>Благодарим ви, <strong>{data?.employeeName}</strong>! Връщането на оборудването е официално потвърдено.</p>
          <p style={{ color: '#888', fontSize: '0.85rem', marginTop: 16 }}>
            Your IP address has been recorded as proof of confirmation.
            You may close this window.
          </p>
        </div>
      </div>
    </div>
  );

  // status === 'ready'
  const items = Object.entries(ITEM_LABELS).filter(([k]) => data[k] && data[k] !== 'NA');

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>Equipment Return Protocol</h2>
          <p style={{ color: '#ffcdd2', margin: '4px 0 0', fontSize: '0.9rem' }}>
            Протокол за връщане на фирмено имущество
          </p>
        </div>
        <div style={styles.body}>
          <div style={styles.infoGrid}>
            <div style={styles.infoRow}><span style={styles.infoLabel}>Employee / Служител</span><span style={styles.infoVal}>{data.employeeName}</span></div>
            <div style={styles.infoRow}><span style={styles.infoLabel}>Employee ID / Сл. №</span><span style={styles.infoVal}>{data.employeeId}</span></div>
            <div style={styles.infoRow}><span style={styles.infoLabel}>Department / Отдел</span><span style={styles.infoVal}>{data.departmentName || '—'}</span></div>
            <div style={styles.infoRow}><span style={styles.infoLabel}>Returned on / Върнато на</span><span style={styles.infoVal}>{fmtDate(data.returnedAt)}</span></div>
          </div>

          {items.length > 0 && (
            <div style={{ margin: '16px 0' }}>
              <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Returned Equipment / Върнато оборудване</p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={styles.th}>Item / Артикул</th>
                    <th style={styles.th}>Serial / Сериен №</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(([k, label]) => (
                    <tr key={k}>
                      <td style={styles.td}>{label}</td>
                      <td style={styles.td}>{data[k]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.returnNotes && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 6, padding: '10px 14px', margin: '12px 0', fontSize: '0.88rem' }}>
              <strong>Notes / Бележки:</strong> {data.returnNotes}
            </div>
          )}

          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '16px', margin: '20px 0' }}>
            <p style={{ fontWeight: 600, color: '#7f1d1d', marginTop: 0 }}>
              ✍ Please type your full name to confirm / Въведете пълното си име за потвърждение:
            </p>
            <input
              type="text"
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              placeholder={data.employeeName}
              style={{ width: '100%', padding: '10px 12px', fontSize: '1rem', border: '2px solid #fca5a5', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }}
            />
            {nameError && <p style={{ color: '#b91c1c', marginTop: 6, fontSize: '0.88rem' }}>⚠ {nameError}</p>}
            <p style={{ color: '#666', fontSize: '0.78rem', marginTop: 8 }}>
              By confirming, your IP address and timestamp will be recorded as legal proof under eIDAS Regulation (EU) 910/2014.
              <br />
              С потвърждаването, вашият IP адрес и дата/час ще бъдат записани съгласно Регламент eIDAS (ЕС) 910/2014.
            </p>
          </div>

          <button
            onClick={handleConfirm}
            disabled={submitting}
            style={{
              width: '100%', padding: '14px', background: submitting ? '#999' : '#7b0000',
              color: '#fff', border: 'none', borderRadius: 8, fontSize: '1.05rem',
              fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer'
            }}
          >
            {submitting ? 'Confirming…' : '✍ Confirm Return / Потвърждавам връщането'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px' },
  card: { width: '100%', maxWidth: 600, borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', background: '#fff' },
  header: { background: 'linear-gradient(135deg,#7b0000,#b71c1c)', padding: '24px 32px' },
  headerTitle: { color: '#fff', margin: 0, fontSize: '1.3rem' },
  body: { padding: '24px 28px' },
  infoGrid: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 },
  infoRow: { display: 'flex', gap: 12, fontSize: '0.9rem' },
  infoLabel: { color: '#6b7280', minWidth: 160, fontWeight: 600 },
  infoVal: { color: '#111827' },
  th: { padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.85rem' },
  td: { padding: '7px 12px', borderBottom: '1px solid #e5e7eb', color: '#374151' },
};
